import type {Placement} from '@react-types/overlays';
import type {RefObject, HTMLAttributes} from 'react';
import {useEffect, useRef, useCallback, useReducer} from 'react';
import {useIsomorphicLayoutEffect} from '@sail/engine';
import {calculatePosition} from './vendor/calculatePosition';
import {
  walkAncestors,
  isScrollable,
  isFixed,
  isOverflowingAxis,
} from './walk-ancestors';
import {useIntersectionObserver} from '../hooks/useIntersectionObserver';
import {useResizeObserver} from '../hooks/useResizeObserver';
import {useMutationObserver} from '../hooks/useMutationObserver';

import type {PositionResult} from './vendor/calculatePosition';
import {getOwnerDocument, getOwnerWindow} from '../utils/getOwnerWindow';

// TODO:
// * Right now the only way to create inner "windows" is by creating
//   a scrollable container. We'll automatically detect it, but we
//   should also provide a way to manually create one
// * Support configuring the behavior. Allow close on scroll/resize
//   when globally mounted?

type PositionReason = 'resize' | 'scroll' | 'shift' | null;

type LayerState = {
  position: PositionResult | null;
  reason: PositionReason;
  portal: boolean;
  placement: Placement;
};

type LayerAction =
  | {
      type: 'switch-portal';
      portal: boolean;
      placement?: Placement;
    }
  | {
      type: 'reposition';
      reason: PositionReason;
      position: PositionResult;
    };

type Input = {
  targetRef: RefObject<HTMLElement>;
  overlayRef: RefObject<HTMLElement>;
  placement?: Placement;
  shouldFlip?: boolean;
  shouldUpdatePosition?: boolean;
  shouldRespectScrollBoundary?: boolean;
  onClose: () => void;
};

type Output = {
  state: LayerState;
  props: HTMLAttributes<Element>;
  container: HTMLElement | null;
};

function translateRTL(position: Placement, direction: string): Placement {
  if (direction === 'rtl') {
    return position
      .replace('start', 'right')
      .replace('end', 'left') as Placement;
  }
  return position.replace('start', 'left').replace('end', 'right') as Placement;
}

const SUPPORTS_SHADOW_ROOT = typeof ShadowRoot !== 'undefined';

export function useLayerPosition({
  targetRef,
  overlayRef,
  placement: requestedPlacement = 'bottom' as Placement,
  shouldFlip = true,
  shouldUpdatePosition = true,
  shouldRespectScrollBoundary = true,
  onClose,
}: Input): Output {
  const boundaryRef = useRef<HTMLElement | null>(null);
  const scrollableRef = useRef<HTMLElement | null>(null);

  const [state, dispatch] = useReducer(
    (state: LayerState, action: LayerAction): LayerState => {
      switch (action.type) {
        case 'switch-portal': {
          return {
            ...state,
            portal: action.portal,
            placement: action.placement || state.placement,
          };
        }
        case 'reposition': {
          return {
            ...state,
            reason: action.reason,
            position: action.position,
          };
        }
        default:
          return state;
      }
    },
    // We track the placement to request in state so we can change it;
    // we don't always want to request the original placement coming
    // from props. If we are switching into a portal, it should
    // *always* stay in the same place, even if there is now room for
    // the layer to not be flipped. When switching into a portal, it
    // changes the `placement` state to whatever it is currently which
    // forced it to stay in place
    {
      position: null,
      reason: null,
      portal: false,
      placement: requestedPlacement,
    },
  );

  const updatePositionRef = useRef<((reason: PositionReason) => void) | null>(
    null,
  );

  useIsomorphicLayoutEffect(() => {
    updatePositionRef.current = (reason: PositionReason) => {
      if (
        shouldUpdatePosition === false ||
        !overlayRef.current ||
        !targetRef.current ||
        !boundaryRef.current
      ) {
        return;
      }

      const result = calculatePosition({
        placement: translateRTL(state.placement, 'ltr'),
        overlayNode: overlayRef.current,
        targetNode: targetRef.current,
        scrollNode: overlayRef.current,
        padding: 0,
        shouldFlip,
        boundaryElement: state.portal
          ? getOwnerDocument(boundaryRef.current).body
          : boundaryRef.current,
        offset: 0,
        crossOffset: 0,
      });

      dispatch({type: 'reposition', reason, position: result});
    };
  }, [
    state.placement,
    overlayRef,
    targetRef,
    shouldFlip,
    shouldUpdatePosition,
    state.portal,
  ]);

  const {reason} = state;
  const {fit, placement, crossPlacement} = state.position || {};

  const axis =
    requestedPlacement.includes('top') || requestedPlacement.includes('bottom')
      ? 'y'
      : 'x';

  // This is the critical effect. This initializes the refs and calls
  // `updatePosition`, both the first time and on all future state
  // changes (like if `portal` changes)
  useIsomorphicLayoutEffect(() => {
    const target = targetRef.current;

    // Reset the node refs
    scrollableRef.current = null;
    boundaryRef.current = null;
    // Find various ancestor nodes
    walkAncestors(target, (el) => {
      if (isScrollable(el) && isOverflowingAxis(el, axis)) {
        scrollableRef.current = el;

        // If we have a scrollable that is overflowing on the current access
        // and should respect the scroll boundary, use the scrollable as the boundary
        if (shouldRespectScrollBoundary) {
          boundaryRef.current = el;
          return true;
        }
      }

      if (
        isFixed(el) ||
        (SUPPORTS_SHADOW_ROOT && el.parentNode instanceof ShadowRoot)
      ) {
        boundaryRef.current = el;

        // If we hit a fixed element or child of a ShadowRoot node, we always want to stop walking
        return true;
      }
    });

    // Always default to document.body
    boundaryRef.current =
      boundaryRef.current || getOwnerDocument(boundaryRef.current).body;

    // If the boundary is scrollable, we don't switch to portal yet because we want
    // to try to fit the content inside the scrollable
    if (!state.portal && boundaryRef.current !== scrollableRef.current) {
      dispatch({type: 'switch-portal', portal: true});
    } else {
      updatePositionRef.current && updatePositionRef.current(null);
    }
  }, [targetRef, state.portal, shouldRespectScrollBoundary]);

  // If we are portalling, we need to check if the target is
  // still visible
  // Also, in the case that this javascript is executed from an iframe, we do not want to
  // assume that we are "portalling", i.e. we do not want the intersection observer to be active
  // since it will be intersecting against the iframe's viewport instead of the top level frame.
  useIntersectionObserver({
    node: targetRef.current,
    threshold: 0.9,
    disabled: !(state.portal || reason === 'resize') || window.top !== window,
    handler: useCallback(
      (entry) => {
        if (entry.intersectionRatio < 0.9) {
          onClose();
        }
      },
      [onClose],
    ),
  });

  useResizeObserver({
    node: state.portal
      ? getOwnerWindow(boundaryRef.current)
      : boundaryRef.current,
    avoidInitialEvent: true,
    handler: useCallback(() => {
      updatePositionRef.current && updatePositionRef.current('resize');
    }, []),
  });

  useEffect(() => {
    const scrollable = scrollableRef.current;
    if (state.portal && scrollable) {
      const onScroll = () => {
        // Update the position of the popover
        updatePositionRef.current && updatePositionRef.current('scroll');
      };

      scrollable.addEventListener('scroll', onScroll);
      return () => scrollable.removeEventListener('scroll', onScroll);
    }
  }, [state.portal, scrollableRef]);

  useMutationObserver({
    node: boundaryRef.current,
    handler: useCallback(
      (mutations) => {
        const overlay = overlayRef.current;
        const remeasure = mutations.some((mutation) => {
          return overlay && !overlay.contains(mutation.target);
        });

        if (remeasure) {
          updatePositionRef.current && updatePositionRef.current('shift');
        }
      },
      [overlayRef],
    ),
  });

  useIsomorphicLayoutEffect(() => {
    if (reason !== 'scroll' && fit === false && !state.portal) {
      dispatch({
        type: 'switch-portal',
        portal: !fit,
        placement: `${placement} ${crossPlacement}` as Placement,
      });
    }
  }, [placement, crossPlacement, reason, fit, state.portal]);

  let style;
  if (isFixed(boundaryRef.current) && state.portal) {
    // If we are positioning against a fixed boundary, we never want to scroll
    // with the page. The positioning should be fixed as well, and to do that we
    // need to subtract the current scroll offset which was included in the
    // calculations.
    // eslint-disable-next-line @sail/ui-no-reflow
    const top = getOwnerDocument(boundaryRef.current).documentElement.scrollTop;
    const left =
      // eslint-disable-next-line @sail/ui-no-reflow
      getOwnerDocument(boundaryRef.current).documentElement.scrollLeft;

    const position = state.position ? {...state.position.position} : null;
    if (position?.top) {
      position.top -= top;
    }
    if (position?.left) {
      position.left -= left;
    }
    if (position?.bottom) {
      position.bottom += top;
    }
    if (position?.right) {
      position.right += left;
    }

    style = {position: 'fixed' as const, ...position};
  } else {
    style = {position: 'absolute' as const, ...state.position?.position};
  }

  return {
    container: state.portal ? null : boundaryRef.current,
    state,
    props: {style},
  };
}
